import React, { FunctionComponent, SyntheticEvent, useEffect, useMemo, useState } from "react";
import BootstrapTable, { SortOrder, TableChangeState, TableChangeType } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import {
  changeSelectedTeacher,
  openShareDataModal,
  openTeacherDetailsModal,
  setNewInviteData,
} from "../../../../../../store/onboarding/actions";
import { bindActionCreators, Dispatch } from "redux";
import { connect, useDispatch } from "react-redux";
import { ApplicationState } from "../../../../../../store";
import { DataSharingTableData, InviteStatuses, School, SendInviteRequest, TeacherInfo } from "../../../../../../store/onboarding/types";
import {
  getFullName,
  getInviteStatusDisplay,
  getUserAssigmentRoleDisplayedName,
} from "../../../../../../utils/NamesUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import { UserAssignmentRole } from "../../../../../../store/auth/types";
import UserStatusButton from "./UserStatusButton";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ResendInviteButton from "./ResendInviteButton";
import _ from "lodash";
import LoadingIndicator from "../../../../../common/onboarding/LoadingIndicator";
import NoStaffForm from "../NoStaffForm";

type StateProps = {
  schoolFilter?: School;
  roleFilter?: UserAssignmentRole;
  searchFilter?: string;
  staffRoster: Array<TeacherInfo>;
  selectedTeacher?: TeacherInfo;
};
type DispatchProps = {
  setSelectedTeacher: (selectedTeacher?: TeacherInfo) => any;
  openTeacherDetailsModal: () => any;
  setNewInviteData: (invite: SendInviteRequest) => any;
  openShareDataModal: (inviteFromFile: string) => any;
};
type OwnProps = {
  noStaffMembers: boolean;
  isLoadingStaff: boolean;
};

type Props = OwnProps & StateProps & DispatchProps;

const StaffTable: FunctionComponent<Props> = ({
  roleFilter,
  searchFilter,
  schoolFilter,
  staffRoster,
  noStaffMembers,
  isLoadingStaff,
  setSelectedTeacher,
  openTeacherDetailsModal,
  setNewInviteData,
  openShareDataModal,
}) => {
  const [sortedStaffData, setSortedStaffData] = useState<TeacherInfo[]>([]);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  // Helper function to get schools string
  const getSchoolsString = (employee: TeacherInfo): string => {
    return employee.user.profile.current_assignment?.schools
      .map((school) => school.name)
      .join(", ") ?? "";
  };

  // Calculate match priority for intelligent sorting (lower number = higher priority)
  const getMatchPriority = (employee: TeacherInfo, searchTerm: string): number => {
    if (!searchTerm) return 0;

    const query = searchTerm.trim();
    const queryLower = query.toLowerCase();

    const firstName = employee.user.first_name?.toLowerCase().trim() || '';
    const lastName = employee.user.last_name?.toLowerCase().trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const reverseName = `${lastName} ${firstName}`.trim();
    const email = employee.user.email?.toLowerCase().trim() || '';
    const emailDisplay = (email.indexOf("njtss_user_imported") >= 0 || email.indexOf("njtssuser") >= 0)
      ? 'imported from file'
      : email;
    const schoolsStr = getSchoolsString(employee).toLowerCase();
    const accountType = getUserAssigmentRoleDisplayedName(
      employee.user.profile.current_assignment?.role
    )?.toLowerCase() || '';
    const inviteStatus = (employee && employee.invite && employee.invite.status !== "invite_pending")
      ? getInviteStatusDisplay(employee).toLowerCase()
      : 'click to invite';

    // Priority 1: Exact matches
    if (firstName === queryLower || lastName === queryLower || fullName === queryLower || reverseName === queryLower) {
      return 1;
    }

    // Priority 2: Starts with first name or last name
    if (firstName.startsWith(queryLower) || lastName.startsWith(queryLower)) {
      return 2;
    }

    // Priority 3: Full name or reverse full name starts with query
    if (fullName.startsWith(queryLower) || reverseName.startsWith(queryLower)) {
      return 3;
    }

    // Priority 4: Email matches
    if (emailDisplay === queryLower) return 1;
    if (emailDisplay.startsWith(queryLower)) return 4;

    // Priority 5: Account Type matches
    if (accountType === queryLower) return 1;
    if (accountType.startsWith(queryLower)) return 5;

    // Priority 6: School matches (with multi-word support)
    if (schoolsStr) {
      const schools = schoolsStr.split(',').map(s => s.trim());
      for (const school of schools) {
        if (school === queryLower) return 1;
        if (school.startsWith(queryLower)) return 6;
        
        // Word boundary match
        const schoolWords = school.split(/\s+/);
        for (const word of schoolWords) {
          if (word.startsWith(queryLower)) return 6;
        }
      }
    }

    // Priority 7: Invite Status matches
    if (inviteStatus === queryLower) return 1;
    if (inviteStatus.startsWith(queryLower)) return 7;

    return 999; // No match
  };

  // Optimized filter and sort using pre-built arrays (matches StudentsTable logic)
  const getFilteredAndSortedStaff = (staffList: TeacherInfo[], applySearchSort: boolean = true): TeacherInfo[] => {
    const searchTerm = searchFilter?.trim();
    
    if (!searchTerm) {
      return staffList;
    }

    const queryLower = searchTerm.toLowerCase();
    
    // If column sorting is active, only filter, don't sort by search relevance
    if (!applySearchSort) {
      return staffList.filter(employee => {
        const priority = getMatchPriority(employee, searchTerm);
        return priority < 999;
      });
    }
    
    // Build search arrays for efficient matching
    const firstNameMatches: TeacherInfo[] = [];
    const lastNameMatches: TeacherInfo[] = [];
    const otherMatches: TeacherInfo[] = [];

    for (const employee of staffList) {
      const firstName = employee.user.first_name?.toLowerCase().trim() || '';
      const lastName = employee.user.last_name?.toLowerCase().trim() || '';

      // Priority Group 1: First name matches (exact or starts with)
      if (
        firstName === queryLower ||
        firstName.startsWith(queryLower)
      ) {
        firstNameMatches.push(employee);
        continue;
      }

      // Priority Group 2: Last name matches (exact or starts with)
      if (
        lastName === queryLower ||
        lastName.startsWith(queryLower)
      ) {
        lastNameMatches.push(employee);
        continue;
      }

      // Priority Group 3: Full name or other field matches
      const priority = getMatchPriority(employee, searchTerm);
      if (priority < 999) {
        otherMatches.push(employee);
      }
    }

    // Sort within each group by exact match first, then alphabetically
    const sortGroup = (group: TeacherInfo[]) => {
      return group.sort((a, b) => {
        const aPriority = getMatchPriority(a, searchTerm);
        const bPriority = getMatchPriority(b, searchTerm);
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // If same priority, sort by name
        const aName = `${a.user.first_name} ${a.user.last_name}`.toLowerCase();
        const bName = `${b.user.first_name} ${b.user.last_name}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    };

    // Combine results: FIRST NAME matches first, then last name, then others
    return [
      ...sortGroup(firstNameMatches),
      ...sortGroup(lastNameMatches),
      ...sortGroup(otherMatches)
    ];
  };

  const columnEvents = {
    onClick: ( 
      e: React.MouseEvent,
      column: any,
      columnIndex: number,
      row: any
    ) => {
      const selectedTeacher = staffRoster.find(
        (teacher) => teacher.user.id === row.id
      );
      setSelectedTeacher(selectedTeacher!);
      openTeacherDetailsModal();
    },
  };
  const columns = [
    {
      dataField: "name",
      text: "User",
      sort: true,
      //events: columnEvents,
      headerStyle: { width: "20%" },
      formatter: (cell: any, row: any) => row.userNameDisplay 
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      headerStyle: { width: "20%" },
      formatter: (cell: any, row: any) => row.emailDisplay 
    },
    {
      dataField: "role",
      text: "Account Type",
      sort: true,
      headerStyle: { width: "22%" }
    },
    {
      dataField: "schools",
      text: "School(s)",
      sort: true,
      headerStyle: { width: "21%" }
    },
    {
      dataField: "inviteStatus",
      text: "INVITE STATUS",
      sort: true,
      headerStyle: { width: "11%" },
      formatter: (cell: any, row: any) => row.inviteStatusDisplay 
    },
    {
      dataField: "actions",
      text: "Actions",
      events: columnEvents,
      headerStyle: { width: "6%" }
    },
  ];

  const options = useMemo(
    () => ({
      sizePerPageList: [
        {
          text: "20",
          value: 20,
        },
        {
          text: "50",
          value: 50,
        },
        {
          text: "100",
          value: 100,
        },
      ],
    }),
    []
  );

  const defaultSorted: [{ dataField: string; order: SortOrder }] = useMemo(
    () => [
      {
        dataField: "name",
        order: "asc" as SortOrder,
      },
    ],
    []
  );

  // const rowEvents = useMemo(
  //   () => ({
  //     onClick: (e: SyntheticEvent, row: any, rowIndex: number) => {
  //       const selectedTeacher = staffRoster.find(
  //         (teacher) => teacher.user.id === row.id
  //       );
  //       setSelectedTeacher(selectedTeacher!);
  //       openTeacherDetailsModal();
  //     },
  //   }),
  //   [staffRoster]
  // );

  const filterStaffRosterBySchool = (employee: TeacherInfo) =>
    !schoolFilter ||
    employee.user.profile?.current_assignment?.schools?.some(
      (school) => school.id === schoolFilter?.id
    );

  const filterStaffRosterByRole = (employee: TeacherInfo) =>
    !roleFilter ||
    employee.user.profile?.current_assignment?.role === roleFilter;

  const handleInviteFromFile = (data: TeacherInfo) => () => {
    const invite: SendInviteRequest = {
      first_name: data.user.first_name,
      last_name: data.user.last_name,
      email: "",
      existing_user_id: data.user.id,
      originalUser: data.user,
      teachers: [],
      grades: [],
    };
    setNewInviteData(invite);
    openShareDataModal('inviteFromFile');
  };
  const handleTableChange = (type: TableChangeType, { sortField: clickedField }: TableChangeState<any>) => {
      if (type === 'sort') {
        let newSortField: string | undefined = clickedField;
        let newSortOrder: 'asc' | 'desc' | undefined = 'asc';
    
        if (sortField === clickedField) {
          if (sortOrder === 'asc') {
            newSortOrder = 'desc';
          } else if (sortOrder === 'desc') {
            // Third click → Clear the sort
            newSortField = undefined;
            newSortOrder = undefined;
          }
        }
    
        setSortField(newSortField);
        setSortOrder(newSortOrder);
      }
    };
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  useEffect(() => {
    const originalData = _.cloneDeep(staffRoster);
    const sortedData = [...originalData];
    sortedData.sort((a, b) => {
      let aValue: any;
      let bValue: any;
  
      switch (sortField) {
        case "name":
          aValue = `${a.user.first_name} ${a.user.last_name}`.trim();
          bValue = `${b.user.first_name} ${b.user.last_name}`.trim();
          break;
        case "email":
          aValue = ((a.user.email.indexOf("njtss_user_imported") >= 0 )
                    ||  (a.user.email.indexOf("njtssuser") >= 0)
                    ? 'Imported From File' : a.user.email);
          bValue = ((b.user.email.indexOf("njtss_user_imported") >= 0 )
                    ||  (b.user.email.indexOf("njtssuser") >= 0)
                    ? 'Imported From File' : b.user.email);
          break;
        case "role": 
          aValue = getUserAssigmentRoleDisplayedName(
            a.user.profile.current_assignment?.role
          );
          bValue = getUserAssigmentRoleDisplayedName(
            b.user.profile.current_assignment?.role
          );
          break;
        case "schools":
          aValue = a.user.profile.current_assignment?.schools
                  .map((school) => school.name)
                  .join(", ") ?? "";
          bValue = b.user.profile.current_assignment?.schools
                  .map((school) => school.name)
                  .join(", ") ?? "";
          break;
        case "inviteStatus":
          aValue = a && a.invite && a.invite.status !== "invite_pending" 
            ? getInviteStatusDisplay(a) 
            : 'Click to Invite';
          bValue = b && b.invite && b.invite.status !== "invite_pending" 
            ? getInviteStatusDisplay(b) 
            : 'Click to Invite';
          break;
        default:
          return 0;
      }
      const isEmpty = (val: any) => val === null || val === undefined || val === 'N/A';
        
      if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
      if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
      if (isEmpty(aValue) && isEmpty(bValue)) return 0;
      
      aValue = aValue.toString().trim();
      bValue = bValue.toString().trim();
  
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? collator.compare(aValue, bValue)
          : collator.compare(bValue, aValue);
      }
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
    setSortedStaffData(sortedData);
    const classListDesc = (document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>)?.[0];
    const classListAsc = (document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>)?.[0];
    if(sortOrder === 'asc') { 
      if(classListAsc){
        classListAsc.style.display = 'inline-block';
      }
    } else if (sortOrder === 'desc') {
      if(classListDesc){
        classListDesc.style.display = 'inline-block';
      }
    } else if(!sortField && !sortOrder) {
      if(classListAsc){
        classListAsc.style.display = 'none';
      }
      setSortField(undefined);
      setSortOrder(undefined);
      setSortedStaffData(originalData);
    }
    
  }, [staffRoster, sortField, sortOrder]);
  
  const data = useMemo(
    () => {
      // First apply school and role filters
      let filteredData = sortedStaffData
        .filter(filterStaffRosterBySchool)
        .filter(filterStaffRosterByRole);
      
      // Apply search filter: if column sort is active, only filter (don't sort by search relevance)
      const hasColumnSort = sortField && sortOrder;
      filteredData = getFilteredAndSortedStaff(filteredData, !hasColumnSort);

      return filteredData.map((employee) => ({
          id: employee.user.id,
          name: `${employee.user.first_name} ${employee.user.last_name}` || "", // Store string value
          userNameDisplay: <h4 className="blueTitle"> {getFullName(employee.user)}</h4>,
          email: ((employee.user.email.indexOf("@njtss_user_imported") >= 0)
                  || (employee.user.email.indexOf("@njtss") >= 0))
                  ? 'Imported From File'  : employee.user.email,
          emailDisplay: ((employee.user.email.indexOf("@njtss_user_imported") >= 0)
                    || (employee.user.email.indexOf("@njtss") >= 0)) 
                    ? (
                        <i>Imported From File</i>
                      ) 
                    : (
                        <div className="multiline-break">{employee.user.email}</div>
                      ),
          role: getUserAssigmentRoleDisplayedName(
            employee.user.profile.current_assignment?.role
          ),
          schools:
            employee.user.profile.current_assignment?.schools
              .map((school) => school.name)
              .join(", ") ?? "",
          inviteStatus:  
            employee && employee.invite && employee.invite.status !== "invite_pending" 
            ? getInviteStatusDisplay(employee) 
            : 'Click to Invite',
          inviteStatusDisplay: (
            <>
              {employee && employee.invite && employee.invite.status !== "invite_pending" ? (
                <div>{getInviteStatusDisplay(employee)}</div>
              ) : (
                <div>
                  <div
                    className="tableRowBtn" style={{width: "120px"}}
                    onClick={handleInviteFromFile(employee)}
                  >
                    Click to Invite
                  </div>
                </div>
              )}
            </>
          ),
          actions: (
            <div className="row">
              <div className="col-2">
                <ResendInviteButton
                  isPending={getInviteStatusDisplay(employee).replace(' ','_').toLowerCase() == InviteStatuses.INVITE_SENT
                    || getInviteStatusDisplay(employee).replace(' ','_').toLowerCase() == InviteStatuses.INVITE_EXPIRED
                  }
                  employee={employee}
                />
              </div>
              <div className="col-2">
                <OverlayTrigger
                  overlay={
                    <Tooltip id="edit-user-btn" className="customInfoTooltip">
                      Edit User
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faPen} size="lg" />
                </OverlayTrigger>
              </div>
              {/* <div className="col-2"> //RC-1457 hide the icon when active to disable from modal
                <UserStatusButton
                  isActive={!employee.user.account_disabled}
                  userId={employee.user.id}
                />
              </div> */}
            </div>
          ),
        }));
    },
    [sortedStaffData, schoolFilter, roleFilter, searchFilter, sortField, sortOrder]
  );

 

  return (
    <>
      {isLoadingStaff ? (
          <LoadingIndicator type="table" rows={20} columns={7}/>
      ) : !isLoadingStaff
        ? (noStaffMembers) ? (<NoStaffForm />)
          : (
            <BootstrapTable
              remote={{ sort: true }}
              classes="rosterTable rosterTableWidth"
              keyField="id"
              bordered={false}
              bootstrap4
              //rowEvents={rowEvents}
              data={data}
              columns={columns}
              sort={sortField && sortOrder ? {
                dataField: sortField,
                order: sortOrder,
              } : undefined}
              defaultSorted={defaultSorted}
              onTableChange={handleTableChange}
              pagination={getFilteredAndSortedStaff(
                sortedStaffData
                  .filter(filterStaffRosterBySchool)
                  .filter(filterStaffRosterByRole),
                false // Only filter for pagination count, don't sort by search relevance
              ).length > 20 ? paginationFactory(options) : undefined}
              noDataIndication={() => "No Users found. Try changing the filters."}
            />
          )
        : ''}
    </>
  );
};

const mapStateToProps = ({
  onboarding,
  usersManagement,
}: ApplicationState): StateProps => {
  return {
    schoolFilter: usersManagement.schoolFilter,
    roleFilter: usersManagement.roleFilter,
    searchFilter: usersManagement.searchFilter,
    staffRoster: onboarding.teachersRoster,
    selectedTeacher: onboarding.selectedTeacher,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      setSelectedTeacher: changeSelectedTeacher,
      openTeacherDetailsModal: openTeacherDetailsModal,
      setNewInviteData: setNewInviteData, 
      openShareDataModal: openShareDataModal
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(StaffTable);
